import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Star, Phone, Mail, Globe, Clock, Heart, ChevronLeft, ChevronRight, Users, Send, Shield, CheckCircle, Disc3, Calendar, Gamepad2, Play, Trophy } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import ShareButtons from '../components/ShareButtons';
import { ClubNotificationButton } from '../components/ClubNotificationButton';

import { API_URL } from '../utils/api';

export default function ClubDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [club, setClub] = useState(null);
  const [instructors, setInstructors] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [events, setEvents] = useState([]);
  const [bingoGames, setBingoGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);
  const [newReview, setNewReview] = useState({ rating: 5, comment: '' });
  const [submittingReview, setSubmittingReview] = useState(false);

  useEffect(() => {
    fetchClubData();
    if (user) checkFavorite();
  }, [id, user]);

  // Fetch bingo games when club data is loaded (for club owners)
  useEffect(() => {
    if (club && user && user.user_id === club.owner_id) {
      fetchBingoGames();
    }
  }, [club, user]);

  const fetchClubData = async () => {
    setLoading(true);
    try {
      const [clubRes, instructorsRes, reviewsRes, eventsRes] = await Promise.all([
        fetch(`${API_URL}/api/clubs/${id}`),
        fetch(`${API_URL}/api/clubs/${id}/instructors`),
        fetch(`${API_URL}/api/clubs/${id}/reviews`),
        fetch(`${API_URL}/api/clubs/${id}/events`)
      ]);

      if (clubRes.ok) {
        setClub(await clubRes.json());
      }
      if (instructorsRes.ok) {
        setInstructors(await instructorsRes.json());
      }
      if (reviewsRes.ok) {
        setReviews(await reviewsRes.json());
      }
      if (eventsRes.ok) {
        setEvents(await eventsRes.json());
      }
    } catch (error) {
      console.error('Error fetching club:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBingoGames = async () => {
    try {
      const response = await fetch(`${API_URL}/api/bingo/clubs/${club.club_id}/games`);
      if (response.ok) {
        setBingoGames(await response.json());
      }
    } catch (error) {
      console.error('Error fetching bingo games:', error);
    }
  };

  const checkFavorite = async () => {
    try {
      const response = await fetch(`${API_URL}/api/favorites/check/${id}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setIsFavorite(data.is_favorite);
      }
    } catch (error) {
      console.error('Error checking favorite:', error);
    }
  };

  const toggleFavorite = async () => {
    if (!user) {
      toast.error('Please sign in to save favorites');
      return;
    }

    try {
      const method = isFavorite ? 'DELETE' : 'POST';
      const response = await fetch(`${API_URL}/api/favorites/${id}`, {
        method,
        credentials: 'include'
      });

      if (response.ok) {
        setIsFavorite(!isFavorite);
        toast.success(isFavorite ? 'Removed from favorites' : 'Added to favorites');
      }
    } catch (error) {
      toast.error('Failed to update favorites');
    }
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Please sign in to leave a review');
      return;
    }

    setSubmittingReview(true);
    try {
      const response = await fetch(`${API_URL}/api/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          club_id: id,
          rating: newReview.rating,
          comment: newReview.comment
        })
      });

      if (response.ok) {
        const review = await response.json();
        setReviews([review, ...reviews]);
        setNewReview({ rating: 5, comment: '' });
        toast.success('Review submitted!');
        // Refresh club to get updated rating
        const clubRes = await fetch(`${API_URL}/api/clubs/${id}`);
        if (clubRes.ok) setClub(await clubRes.json());
      }
    } catch (error) {
      toast.error('Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  const renderStars = (rating, size = 16, interactive = false) => {
    return [...Array(5)].map((_, i) => (
      <Star
        key={i}
        size={size}
        className={`${i < Math.floor(rating) ? 'fill-violet-500 text-violet-500' : 'text-slate-300'} ${interactive ? 'cursor-pointer hover:text-violet-400' : ''}`}
        onClick={interactive ? () => setNewReview({ ...newReview, rating: i + 1 }) : undefined}
      />
    ));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-500" />
      </div>
    );
  }

  if (!club) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-700 mb-4">Club not found</h1>
          <Link to="/clubs">
            <Button className="bg-violet-600 hover:bg-violet-500">Back to Clubs</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="club-detail-page">
      {/* Header Image */}
      <div className="relative h-64 md:h-96 bg-slate-900">
        <img
          src={club.image_url || 'https://images.unsplash.com/photo-1761858736318-f1fe86aec4db?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBjb3VudHJ5JTIwd2VzdGVybiUyMGRhbmNlJTIwY2x1YiUyMGludGVyaW9yJTIwbmVvbnxlbnwwfHx8fDE3NzIwOTgwMjB8MA&ixlib=rb-4.1.0&q=85'}
          alt={club.name}
          className="w-full h-full object-cover opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent" />
        
        <div className="absolute top-4 left-4 right-4 flex justify-between">
          <Link to="/clubs">
            <Button variant="secondary" className="bg-white shadow-md border border-slate-200 text-slate-700 hover:bg-slate-50" data-testid="back-btn">
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
          </Link>
          <div className="flex gap-2">
            <ClubNotificationButton 
              clubId={club.club_id} 
              clubName={club.name}
              showLabel={false}
            />
            <Button
              variant="secondary"
              className={`bg-white shadow-md border border-slate-200 hover:bg-slate-50 ${isFavorite ? 'text-violet-600 border-violet-300' : 'text-slate-700'}`}
              onClick={toggleFavorite}
              data-testid="favorite-btn"
            >
              <Heart className={`w-5 h-5 ${isFavorite ? 'fill-violet-500 text-violet-600' : ''}`} />
            </Button>
            <ShareButtons 
              title={club?.name}
              description={club?.description}
              type="club"
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 lg:px-12 -mt-24 relative z-10 pb-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column - Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Club Info Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl shadow-xl p-6 md:p-8"
            >
              <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                <div>
                  {club.is_featured && (
                    <Badge className="bg-violet-500 text-white mb-2">Featured</Badge>
                  )}
                  <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900" style={{ fontFamily: 'Syne, sans-serif' }} data-testid="club-name">
                    {club.name}
                  </h1>
                  <p className="flex items-center text-slate-400 mt-2">
                    <MapPin className="w-5 h-5 mr-1" />
                    {club.location || [club.city, club.region, club.country].filter(Boolean).join(', ') || 'Location not specified'}
                  </p>
                  {/* Additional Locations */}
                  {club.additional_locations && club.additional_locations.length > 0 && (
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                      {club.additional_locations.map((loc, index) => (
                        <span key={index} className="inline-flex items-center text-sm text-slate-400 bg-slate-100 px-2 py-1 rounded-full">
                          <MapPin className="w-3 h-3 mr-1" />
                          {loc.address}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex">{renderStars(club.rating)}</div>
                  <span className="text-lg font-bold text-slate-900">{club.rating.toFixed(1)}</span>
                  <span className="text-slate-400">({club.review_count} reviews)</span>
                </div>
              </div>

              {/* Verification Badges */}
              {(club.verified_accredited || club.verified_insurance || club.verified_ppl || club.verified_safeguarding) && (
                <div className="flex flex-wrap gap-2 mb-6" data-testid="verification-badges">
                  {club.verified_accredited && (
                    <div className="group relative inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full text-sm font-medium">
                      <Shield className="w-4 h-4" />
                      <span className="hidden sm:inline">Accredited</span>
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-slate-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                        Accredited Instructor
                        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-900"></div>
                      </div>
                    </div>
                  )}
                  {club.verified_insurance && (
                    <div className="group relative inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-50 text-green-700 rounded-full text-sm font-medium">
                      <CheckCircle className="w-4 h-4" />
                      <span className="hidden sm:inline">Insured</span>
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-slate-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                        Insurance Verified
                        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-900"></div>
                      </div>
                    </div>
                  )}
                  {club.verified_ppl && (
                    <div className="group relative inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 text-purple-700 rounded-full text-sm font-medium">
                      <Disc3 className="w-4 h-4" />
                      <span className="hidden sm:inline">PPL</span>
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-slate-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                        PPL Music License
                        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-900"></div>
                      </div>
                    </div>
                  )}
                  {club.verified_safeguarding && (
                    <div className="group relative inline-flex items-center gap-1.5 px-3 py-1.5 bg-orange-50 text-orange-700 rounded-full text-sm font-medium">
                      <Users className="w-4 h-4" />
                      <span className="hidden sm:inline">Under 18s</span>
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-slate-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                        Under 18s Safeguarding (DBS Checked)
                        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-900"></div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <p className="text-slate-600 text-lg mb-6" data-testid="club-description">{club.description}</p>
              
              {/* DJ Console Access for Club Owners/Instructors - only if DJ Live is enabled */}
              {club.dj_live_enabled && user && (user.user_id === club.owner_id || instructors.some(i => i.user_id === user.user_id)) && (
                <div className="flex flex-wrap gap-3 pt-4 border-t border-slate-100">
                  <Link to={`/club/${club.slug || club.club_id}/dj-console`}>
                    <Button className="bg-violet-600 hover:bg-violet-500">
                      <Disc3 className="w-4 h-4 mr-2" />
                      DJ Console
                    </Button>
                  </Link>
                  <Link to={`/club/${club.slug || club.club_id}/dj-live`} target="_blank">
                    <Button variant="outline" className="border-violet-300 text-violet-600 hover:bg-violet-50">
                      View Dancer Page
                    </Button>
                  </Link>
                </div>
              )}
            </motion.div>

            {/* Class Schedule - Moved here between details and reviews */}
            {club.schedule?.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
                className="bg-white rounded-2xl shadow-xl p-6 md:p-8"
              >
                <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center" style={{ fontFamily: 'Syne, sans-serif' }}>
                  <Clock className="w-6 h-6 mr-2 text-violet-500" />
                  Class Schedule
                </h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {club.schedule.map((item, i) => (
                    <div key={i} className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                      <div className="flex justify-between items-start mb-2">
                        <span className="font-semibold text-slate-900">{item.day}</span>
                        <div className="flex flex-wrap gap-1 justify-end">
                          {(item.level || 'All Levels').split(' & ').map((lvl, idx) => (
                            <span 
                              key={idx}
                              className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                lvl === 'Absolute Beginner' ? 'bg-emerald-100 text-emerald-700' :
                                lvl === 'Beginner' ? 'bg-green-100 text-green-700' :
                                lvl === 'Improver' ? 'bg-yellow-100 text-yellow-700' :
                                lvl === 'Intermediate' ? 'bg-orange-100 text-orange-700' :
                                lvl === 'Advanced' ? 'bg-red-100 text-red-700' :
                                'bg-violet-100 text-violet-700'
                              }`}
                            >
                              {lvl}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="text-sm text-slate-600 space-y-1">
                        <div className="flex items-center gap-1.5">
                          <Clock className="w-4 h-4 text-violet-400" />
                          <span>{item.time}</span>
                        </div>
                        {item.venue && (
                          <div className="flex items-center gap-1.5">
                            <MapPin className="w-4 h-4 text-violet-400" />
                            <span>{item.venue}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Reviews */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl shadow-xl p-6 md:p-8"
            >
              <h2 className="text-xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Syne, sans-serif' }}>
                Reviews ({reviews.length})
              </h2>

              {/* Add Review Form */}
              {user && (
                <form onSubmit={handleReviewSubmit} className="mb-8 p-4 bg-slate-50 rounded-xl" data-testid="review-form">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-sm font-medium text-slate-700">Your Rating:</span>
                    <div className="flex gap-1">{renderStars(newReview.rating, 20, true)}</div>
                  </div>
                  <Textarea
                    value={newReview.comment}
                    onChange={(e) => setNewReview({ ...newReview, comment: e.target.value })}
                    placeholder="Share your experience..."
                    className="mb-3"
                    rows={3}
                    data-testid="review-comment-input"
                  />
                  <Button 
                    type="submit" 
                    disabled={submittingReview || !newReview.comment.trim()}
                    className="bg-violet-600 hover:bg-violet-500"
                    data-testid="submit-review-btn"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {submittingReview ? 'Submitting...' : 'Submit Review'}
                  </Button>
                </form>
              )}

              {/* Reviews List */}
              {reviews.length === 0 ? (
                <p className="text-slate-400 text-center py-8">No reviews yet. Be the first to review!</p>
              ) : (
                <div className="space-y-4">
                  {reviews.map(review => (
                    <div key={review.review_id} className="border-b border-slate-100 pb-4 last:border-0" data-testid={`review-${review.review_id}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <Avatar className="w-10 h-10">
                            <AvatarFallback className="bg-slate-200 text-slate-600">
                              {review.user_name.split(' ').map(n => n[0]).join('')}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-semibold text-slate-900">{review.user_name}</p>
                            <div className="flex">{renderStars(review.rating, 12)}</div>
                          </div>
                        </div>
                        <span className="text-sm text-slate-400">
                          {new Date(review.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="text-slate-600">{review.comment}</p>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* Instructors Card - First */}
            {instructors.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
                className="bg-white rounded-2xl shadow-xl p-6"
              >
                <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center" style={{ fontFamily: 'Syne, sans-serif' }}>
                  <Users className="w-5 h-5 mr-2 text-violet-500" />
                  {instructors.length === 1 ? 'Our Instructor' : 'Our Instructors'}
                </h2>
                <div className="space-y-3">
                  {instructors.map(instructor => (
                    <Link 
                      key={instructor.instructor_id} 
                      to={`/instructors/${instructor.instructor_id}`}
                      data-testid={`instructor-${instructor.instructor_id}`}
                    >
                      <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl hover:bg-violet-50 transition-colors group cursor-pointer">
                        <Avatar className="w-12 h-12 flex-shrink-0">
                          <AvatarImage src={instructor.image_url} />
                          <AvatarFallback className="bg-violet-100 text-violet-600 text-sm font-bold">
                            {instructor.name.split(' ').map(n => n[0]).join('')}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0 flex-1">
                          <h3 className="font-semibold text-slate-900 group-hover:text-violet-600 transition-colors text-sm">{instructor.name}</h3>
                          <p className="text-xs text-slate-400 line-clamp-1">{instructor.bio}</p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-violet-500 flex-shrink-0" />
                      </div>
                    </Link>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Contact Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: instructors.length > 0 ? 0.2 : 0.15 }}
              className="bg-white rounded-2xl shadow-xl p-6"
            >
              <h2 className="text-lg font-bold text-slate-900 mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
                Contact Information
              </h2>
              <div className="space-y-4">
                {club.phone && (
                  <a href={`tel:${club.phone}`} className="flex items-center gap-3 text-slate-600 hover:text-violet-600 transition-colors" data-testid="club-phone">
                    <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                      <Phone className="w-5 h-5 text-violet-600" />
                    </div>
                    {club.phone}
                  </a>
                )}
                {club.email && (
                  <a href={`mailto:${club.email}`} className="flex items-center gap-3 text-slate-600 hover:text-violet-600 transition-colors" data-testid="club-email">
                    <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                      <Mail className="w-5 h-5 text-violet-600" />
                    </div>
                    {club.email}
                  </a>
                )}
                {club.website && (
                  <a 
                    href={club.website.startsWith('http') ? club.website : `https://${club.website}`} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="flex items-center gap-3 text-slate-600 hover:text-violet-600 transition-colors" 
                    data-testid="club-website"
                  >
                    <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center shrink-0">
                      <Globe className="w-5 h-5 text-violet-600" />
                    </div>
                    <span className="truncate">{club.website.replace(/^https?:\/\//, '')}</span>
                  </a>
                )}
              </div>
            </motion.div>

            {/* Events Card */}
            {events.filter(e => new Date(e.event_date) >= new Date().setHours(0,0,0,0)).length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-white rounded-2xl shadow-xl p-6"
              >
                <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center" style={{ fontFamily: 'Syne, sans-serif' }}>
                  <Calendar className="w-5 h-5 mr-2 text-violet-500" />
                  Upcoming Events
                </h2>
                <div className="space-y-3">
                  {events
                    .filter(e => new Date(e.event_date) >= new Date().setHours(0,0,0,0))
                    .slice(0, 5)
                    .map((event, i) => {
                    const eventDate = new Date(event.event_date);
                    // Generate SEO-friendly URL
                    const eventUrl = event.full_slug 
                      ? `/events/${event.full_slug}` 
                      : event.slug 
                        ? `/events/${event.slug}` 
                        : `/events/${event.event_id}`;
                    return (
                      <Link 
                        to={eventUrl}
                        key={event.event_id || i}
                        className="block"
                        data-testid={`club-event-${event.event_id}`}
                      >
                        <div 
                          className="p-3 rounded-lg border bg-violet-50 border-violet-200 hover:bg-violet-100 hover:border-violet-300 transition-colors cursor-pointer"
                        >
                          <div className="flex justify-between items-start mb-1">
                            <h3 className="font-semibold text-slate-900 text-sm group-hover:text-violet-700">{event.title || event.name}</h3>
                            {event.source_type === 'community' && (
                              <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700">Community</Badge>
                            )}
                          </div>
                          <div className="text-xs text-slate-600 space-y-1">
                            <div className="flex items-center gap-1">
                              <Calendar className="w-3 h-3 text-slate-400" />
                              {eventDate.toLocaleDateString('en-GB', { 
                                weekday: 'short', 
                                day: 'numeric', 
                                month: 'short',
                                year: 'numeric'
                              })}
                              {event.event_time && ` at ${event.event_time}`}
                            </div>
                            {event.location && (
                              <div className="flex items-center gap-1">
                                <MapPin className="w-3 h-3 text-slate-400" />
                                {event.location}
                              </div>
                            )}
                            {event.price && (
                              <div className="text-violet-600 font-medium">£{event.price}</div>
                            )}
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
                {events.filter(e => new Date(e.event_date) >= new Date().setHours(0,0,0,0)).length > 5 && (
                  <p className="text-xs text-slate-400 mt-3 text-center">
                    + {events.filter(e => new Date(e.event_date) >= new Date().setHours(0,0,0,0)).length - 5} more events
                  </p>
                )}
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
